import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import LessonCanvas from '../LessonCanvas';
import { startLesson } from '../../canvasEngine';
import { knowledgeGraphToLessonSpec } from '../../canvasEngine/knowledgeGraphToLessonSpec';
import { AnimatedLessonService } from '../../services/llmClient/AnimatedLessonService';
import { DEMO_SCENE_TOPICS, getDemoSceneTopicByKey } from '../../data/demoSceneRequests';

function useResizeCanvas(canvasRef) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;

      const rect = parent.getBoundingClientRect();
      const cssW = rect.width;
      const cssH = rect.height;
      if (!cssW || !cssH) return;

      const requestedDpr = window.devicePixelRatio || 1;
      const mobile = window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
      const dpr = Math.min(requestedDpr, mobile ? 1.5 : 2);

      canvas.style.width = `${cssW}px`;
      canvas.style.height = `${cssH}px`;
      canvas.width = Math.round(cssW * dpr);
      canvas.height = Math.round(cssH * dpr);

      const ctx = canvas.getContext('2d');
      if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const ro = new ResizeObserver(resize);
    ro.observe(canvas.parentElement || canvas);
    resize();

    return () => ro.disconnect();
  }, [canvasRef]);
}

const LandingDemoCanvasSection = () => {
  const canvasRef = useRef(null);
  const handleRef = useRef(null);
  useResizeCanvas(canvasRef);

  const lessonSpecRef = useRef(null);
  const [topicKey, setTopicKey] = useState('ai');
  const preset = useMemo(() => getDemoSceneTopicByKey(topicKey), [topicKey]);

  useEffect(() => {
    if (!canvasRef.current) return undefined;

    let isCancelled = false;

    const boot = async () => {
      const req = preset.request;
      let lessonContent = null;

      if (preset.staticSceneData?.treeSlides && preset.staticSceneData?.nodeContentById) {
        const detailsById = new Map(Object.entries(preset.staticSceneData.nodeContentById));
        lessonContent = {
          lessonId: preset.staticSceneData.lessonId || `landing_static_${preset.key}`,
          title: preset.staticSceneData.title || req.topic,
          knowledgeGraph: AnimatedLessonService.buildKnowledgeGraphFromSceneTree(
            preset.staticSceneData.treeSlides,
            detailsById
          ),
        };
      } else {
        const contentCacheKey = AnimatedLessonService.buildLessonContentCacheKey({
          courseId: 'landing_demo',
          unitId: preset.key,
          topic: req.topic,
          lessonObjective: req.lessonObjective,
          presentationMode: 'scene',
        });

        lessonContent = AnimatedLessonService.getCachedLessonContent(contentCacheKey);
        if (!lessonContent?.knowledgeGraph?.length) {
          lessonContent = await AnimatedLessonService.generateSceneSlidesFromTree({
            topic: req.topic,
            lessonId: '',
            courseTitle: req.courseTitle,
            lessonObjective: req.lessonObjective,
            learner: req.learner,
            animation: req.animation,
            contentCacheKey,
            initialNodeCount: typeof window !== 'undefined' && window.innerWidth <= 768 ? 1 : 2,
          });
          AnimatedLessonService.saveLessonContent(contentCacheKey, lessonContent);
        }
      }

      if (isCancelled) return;
      lessonSpecRef.current = knowledgeGraphToLessonSpec(lessonContent, {
        title: lessonContent?.title || req.topic,
        presentationMode: 'scene',
      });

      if (handleRef.current) {
        handleRef.current.destroy();
        handleRef.current = null;
      }

      handleRef.current = startLesson(canvasRef.current, lessonSpecRef.current);
      handleRef.current.setSpeed(1);
    };

    boot().catch(() => {
      // Keep landing stable even if scene API fails.
    });

    return () => {
      isCancelled = true;
      if (handleRef.current) {
        handleRef.current.destroy();
        handleRef.current = null;
      }
    };
  }, [preset]);

  return (
    <section className="landing-demo-canvas">
      <div className="landing-demo-canvas__topic-row">
        <select
          value={topicKey}
          onChange={(e) => setTopicKey(e.target.value)}
          className="landing-demo-canvas__topic-select"
          aria-label="Select demo topic"
        >
          {DEMO_SCENE_TOPICS.map((topic) => (
            <option key={topic.key} value={topic.key}>
              {topic.label}
            </option>
          ))}
        </select>
        <Link
          to={`/try?static=1&topic=${encodeURIComponent(topicKey)}`}
          className="landing-demo-canvas__try-link"
        >
          Try
        </Link>
      </div>
      <a
        href={`/try?static=1&topic=${encodeURIComponent(topicKey)}`}
        className="landing-demo-canvas__stage"
        aria-label="Open full scene demo"
      >
        <LessonCanvas ref={canvasRef} />
        <div className="landing-demo-canvas__expand-icon" aria-hidden="true">
          <i className="fa-solid fa-up-right-and-down-left-from-center" />
        </div>
      </a>
    </section>
  );
};

export default LandingDemoCanvasSection;
